import React, { useState } from 'react';
import {
  Box,
  Text,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Badge,
  Heading,
  Flex,
  Tag,
  useColorModeValue,
  Tooltip,
} from '@chakra-ui/react';

const DataSummaryView = ({ summary, hideHeader = false, colors }) => {
  const [expandedRows, setExpandedRows] = useState({});

  const themeColors = colors || {
    navbarBg: '#0b4e5aff',
    fontColor: '#0b4e5aff',
    borderColor: '#178da1ff',
    buttonColor: '#083c46ff',
    iconColor: '#9acd32',  
    titleIconColor: '#9acd32',
    servicesBg: '#f6f3ec'
  };

  // Colors for the component
  const headerBg = themeColors.navbarBg;
  const headerTextColor = 'white';
  const tableBorderColor = themeColors.borderColor;
  const hoverBg = useColorModeValue('#f0f0f0', 'gray.700');

  if (!summary || !summary.fields) {
    return (
      <Box textAlign="center" py={4}>
        <Text>No summary data available</Text>
      </Box>
    );
  }

  // Determine data type color based on the dtype
  const getTypeColor = (dtype) => {
    switch (dtype?.toLowerCase()) {
      case 'string':
      case 'object':
        return 'purple';
      case 'number':
      case 'int':
      case 'integer':
      case 'float':
        return 'blue';
      case 'boolean':
      case 'bool':
        return 'orange';
      case 'date':
      case 'datetime':
        return 'teal';
      case 'category':
        return 'green';
      default:
        return 'gray';
    }
  };

  // Toggle row expansion
  const toggleRowExpansion = (rowId) => {
    setExpandedRows(prev => ({
      ...prev,
      [rowId]: !prev[rowId]
    }));
  };

  // Format sample values based on data type
  const formatSample = (sample, dtype) => {
    if (sample === null || sample === undefined) return 'null';
    if (Array.isArray(sample)) return `[${sample.join(', ')}]`;
    if (typeof sample === 'object') return JSON.stringify(sample);
    return String(sample);
  };

  return (
    <Box width="100%">
      {!hideHeader && (
        <Heading as="h3" size="md" mb={4} color={themeColors.fontColor}>
          <Box as="span" color={themeColors.titleIconColor} mr={2}>📋</Box>
          Data Fields
        </Heading>
      )}

      <Box overflowX="auto" borderRadius="md" shadow="md">
        <Table variant="simple" size="sm" borderWidth="1px" borderColor={tableBorderColor}>
          <Thead bg={headerBg}>
            <Tr>
              <Th color={headerTextColor}>Column</Th>
              <Th color={headerTextColor}>Type</Th>
              <Th color={headerTextColor}>Description</Th>
              <Th color={headerTextColor}>Sample Values</Th>
            </Tr>
          </Thead>
          <Tbody>
            {summary.fields.map((field, index) => (
              <Tr 
                key={field.column || index}
                cursor="pointer"
                _hover={{ bg: hoverBg }}
                onClick={() => toggleRowExpansion(field.column)}
              >
                <Td fontWeight="medium">{field.column}</Td>
                <Td>
                  <Badge 
                    colorScheme={getTypeColor(field.properties.dtype)} 
                    px={2} 
                    py={1} 
                    borderRadius="md"
                  >
                    {field.properties.dtype || 'unknown'}
                  </Badge>
                </Td>
                <Td maxWidth="300px" isTruncated>
                  <Tooltip label={field.properties.description} isDisabled={!field.properties.description}>
                    <Text>{field.properties.description || '-'}</Text>
                  </Tooltip>
                </Td>
                <Td>
                  <Flex flexWrap="wrap" gap={1}>
                    {(field.properties.samples || []).slice(0, 3).map((sample, idx) => (
                      <Tag 
                        key={idx} 
                        size="sm" 
                        colorScheme={getTypeColor(field.properties.dtype)}
                        variant="subtle"
                      >
                        {formatSample(sample, field.properties.dtype)}
                      </Tag>
                    ))}
                    {field.properties.samples && field.properties.samples.length > 3 && (
                      <Tag size="sm" colorScheme="gray">+{field.properties.samples.length - 3} more</Tag>
                    )}
                  </Flex>
                </Td>
              </Tr>
            ))}
          </Tbody>
        </Table>
      </Box>
    </Box>
  );
};

export default DataSummaryView;
